import { Button, Input } from "@exhousing/design-system";
import { Check, Circle, ListTodo, MessageSquare } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useActivity, useAddNote, useCompleteNote, useNotes } from "@/hooks/api/useCrm";
import { useUsers } from "@/hooks/api/useUsers";

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const EVENT_LABEL: Record<string, string> = {
  "lead.created": "Lead created",
  "lead.stage_changed": "Moved stage",
  "lead.converted": "Converted to landlord",
  "account.created": "Account created",
};

/** A record's activity: notes (log) and tasks (with due dates + done), newest first.
 *  Shared by the lead and account drawers. */
export function NotesTimeline({
  subjectType,
  subjectId,
}: {
  subjectType: "lead" | "account";
  subjectId: string;
}) {
  const { data: notes } = useNotes(subjectType, subjectId);
  const { data: activity } = useActivity(subjectType, subjectId);
  const { data: users } = useUsers();
  const add = useAddNote();
  const complete = useCompleteNote();

  const actorName = (id: string | null | undefined) => {
    if (!id) return "System";
    const u = (users ?? []).find((x) => x.id === id);
    return u ? [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email : "Someone";
  };

  const [kind, setKind] = useState<"note" | "task">("note");
  const [body, setBody] = useState("");
  const [due, setDue] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    add.mutate(
      {
        subject_type: subjectType,
        subject_id: subjectId,
        kind,
        body: body.trim(),
        due_date: kind === "task" && due ? due : null,
      },
      {
        onSuccess: () => {
          setBody("");
          setDue("");
        },
        onError: () => toast.error("Could not add"),
      },
    );
  };

  // Merge manual notes/tasks with system events into one chronological feed.
  const feed = [
    ...(notes ?? []).map((n) => ({ kind: "note" as const, at: n.created_at, note: n })),
    ...(activity ?? []).map((e) => ({ kind: "event" as const, at: e.occurred_at, event: e })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="space-y-2 rounded-lg border border-border p-3">
        <div className="flex gap-1">
          {(["note", "task"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                kind === k
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {k === "note" ? <MessageSquare className="size-3.5" /> : <ListTodo className="size-3.5" />}
              {k === "note" ? "Note" : "Task"}
            </button>
          ))}
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder={kind === "note" ? "Log a note…" : "What needs doing?"}
          className="min-h-16 w-full resize-y rounded-md border border-input bg-card p-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <div className="flex items-center gap-2">
          {kind === "task" && (
            <Input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              className="h-8 w-40"
              aria-label="Due date"
            />
          )}
          <Button type="submit" size="sm" disabled={add.isPending || !body.trim()}>
            Add {kind}
          </Button>
        </div>
      </form>

      {feed.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {feed.map((item) =>
            item.kind === "note" ? (
              <li key={`n-${item.note.id}`} className="flex gap-2.5">
                {item.note.kind === "task" ? (
                  <button
                    onClick={() => !item.note.is_done && complete.mutate(item.note.id)}
                    disabled={item.note.is_done || complete.isPending}
                    aria-label={item.note.is_done ? "Done" : "Mark done"}
                    className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border transition-colors ${
                      item.note.is_done
                        ? "border-success bg-success text-white"
                        : "border-input hover:border-primary"
                    }`}
                  >
                    {item.note.is_done && <Check className="size-3" />}
                  </button>
                ) : (
                  <MessageSquare className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm ${item.note.is_done ? "text-muted-foreground line-through" : ""}`}
                  >
                    {item.note.body}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.note.kind === "task" && item.note.due_date
                      ? `Due ${fmtDate(item.note.due_date)} · `
                      : ""}
                    {fmtDate(item.note.created_at)}
                  </p>
                </div>
              </li>
            ) : (
              <li key={`e-${item.event.id}`} className="flex gap-2.5">
                <Circle className="mt-1 size-2.5 shrink-0 fill-muted-foreground/40 text-muted-foreground/40" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-muted-foreground">
                    {EVENT_LABEL[item.event.event_type] ??
                      item.event.event_type.replace(/[._]/g, " ")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {actorName(item.event.actor_id)} · {fmtDate(item.event.occurred_at)}
                  </p>
                </div>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
